import { FC, ReactNode } from 'react';
import { createUseStyles } from 'react-jss';
import { BRAND, CHART_INK } from './agencyChartTheme';
import { BeaAskChip } from './BeaAskChip';

const useStyles = createUseStyles({
    // Bea's line under a panel, so the tab reads as an analysis somebody ran rather than a wall of
    // marks the reader is left to interpret alone. Set below the panel and behind a rule, because it
    // is an interpretation of the data rather than a caption on it, and the reader is owed that
    // difference.
    said: {
        display: 'flex',
        gap: 9,
        marginTop: 14,
        paddingTop: 12,
        borderTop: `1px solid ${BRAND.tealEdge}`,
        fontSize: 12.5,
        lineHeight: 1.55,
        color: CHART_INK.label,

        // On paper the figures stand on their own; Bea's read is for the screen, where it can be acted on.
        '@media print': {
            display: 'none'
        }
    },

    line: {
        flex: 1,
        minWidth: 0
    },

    askable: {
        alignItems: 'center',
        width: '100%',
        padding: '12px 10px',
        border: 0,
        borderTop: `1px solid ${BRAND.tealEdge}`,
        borderRadius: '0 0 10px 10px',
        background: 'none',
        fontFamily: 'inherit',
        textAlign: 'left' as const,
        cursor: 'pointer',

        '&:hover, &:focus-visible': {
            background: BRAND.tealWash
        }
    },

    mark: {
        width: 22,
        height: 22,
        flexShrink: 0,
        borderRadius: '50%',
        objectFit: 'cover' as const
    }
});

/** Bea's read on the panel above, in her voice. Given `onAsk`, the whole line asks her about it. */
export const BeaSays: FC<{
    /** Which panel this sits under, so a driver can select one without counting from the top. */
    panel: string;
    onAsk?: () => void;
    children: ReactNode;
}> = ({ panel, onAsk, children }) => {
    const classes = useStyles();
    const mark = <img src="/assets/images/help/helper-bea.png" alt="" className={classes.mark} />;

    // Client names reach these sentences, so the line stays out of session replay. Bea's avatar is
    // a static asset and mp-mask covers text only, so it is unaffected.
    if (onAsk) {
        return (
            <button type="button" className={`${classes.said} ${classes.askable}`} data-id="bea-says" data-panel={panel} onClick={onAsk}>
                {mark}
                <span className={`${classes.line} mp-mask`}>{children}</span>
                <BeaAskChip label="Ask about this" dataId="bea-says-ask" />
            </button>
        );
    }

    return (
        <div className={`${classes.said} mp-mask`} data-id="bea-says" data-panel={panel}>
            {mark}
            <span>{children}</span>
        </div>
    );
};
